import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { toast } from './lib/toast';
import { ConfirmProvider } from './components/ui/confirm';

import AppSidebar from './components/layout/AppSidebar';
import MobileTabBar from './components/layout/MobileTabBar';
import { AlertTriangle } from 'lucide-react';

import AutomationEditor from './components/automations/AutomationEditor';
import AutomationList from './components/automations/AutomationList';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import DeviceManager from './components/devices/DeviceManager';
import SmartLockManager from './components/devices/SmartLockManager';
import SceneControl from './components/scenes/SceneControl';
import SettingsPanel from './components/settings/SettingsPanel';
import FeatureRoute from './components/common/FeatureRoute';
import ProtectedRoute from './components/auth/ProtectedRoute';

import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { FeatureProvider, useFeatures } from './context/FeatureContext';

const AppContent = ({ systemStatus }) => {
  const { loading: featuresLoading } = useFeatures();
  const [collapsed, setCollapsed] = useState(false);

  if (featuresLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
        <h2 className="ml-4">Loading features...</h2>
      </div>
    );
  }

  const sidebarPadding = collapsed ? 'md:pl-[76px]' : 'md:pl-64';

  return (
    <div className="min-h-screen flex flex-col">
      {!systemStatus.online && (
        <div className="bg-destructive/90 text-destructive-foreground px-4 py-2 text-sm text-center">
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle />
            <span>Connection to the smart home system has been lost. Attempting to reconnect...</span>
          </div>
        </div>
      )}

      <main className={`flex-1 pb-20 md:pb-4 pl-0 transition-all duration-300 ${sidebarPadding}`}>
        <Routes>
          <Route path="/" element={<Navigate to="/devices" />} />
          <Route path="/devices" element={
            <FeatureRoute feature="devices" showDisabledMessage>
              <DeviceManager />
            </FeatureRoute>
          } />
          <Route path="/smartlocks" element={
            <FeatureRoute feature="smartLocks" showDisabledMessage>
              <SmartLockManager />
            </FeatureRoute>
          } />
          <Route path="/scenes" element={
            <FeatureRoute feature="scenes" showDisabledMessage>
              <SceneControl />
            </FeatureRoute>
          } />
          <Route path="/automations" element={
            <FeatureRoute feature="automations" showDisabledMessage>
              <AutomationList />
            </FeatureRoute>
          } />
          <Route path="/automations/:automationId" element={
            <FeatureRoute feature="automations" showDisabledMessage>
              <AutomationEditor />
            </FeatureRoute>
          } />
          <Route path="/settings" element={<SettingsPanel />} />
        </Routes>
      </main>

      <AppSidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      <MobileTabBar />
    </div>
  );
};

const API_BASE_URL = '';
const API_PATH = '/api';

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState({
    online: false,
    latency: null,
    lastUpdate: null
  });

  const checkSystemStatus = useCallback(async () => {
    try {
      const startTime = Date.now();
      const response = await fetch(`${API_BASE_URL}${API_PATH}/status`);
      const latency = Date.now() - startTime;

      if (response.ok) {
        const data = await response.json();
        setSystemStatus({
          online: true,
          latency,
          lastUpdate: new Date(),
          ...data
        });

        if (isLoading) setIsLoading(false);
      } else {
        throw new Error('System offline');
      }
    } catch (error) {
      setSystemStatus({
        online: false,
        latency: null,
        lastUpdate: new Date()
      });

      if (isLoading) {
        toast.error('Unable to connect to the smart home system. Please check your connection.');
      }
    }
  }, [isLoading]);

  useEffect(() => {
    checkSystemStatus();
    const statusInterval = setInterval(checkSystemStatus, 30000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [checkSystemStatus]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-accent/20">
        <LoadingSpinner size="large" />
        <h2 className="ml-4">Connecting to Smart Home System...</h2>
      </div>
    );
  }

  return (
    <AuthProvider>
      <FeatureProvider>
        <AppProvider>
          <ErrorBoundary>
            <Router>
              <ProtectedRoute>
                <ConfirmProvider>
                  <div className="min-h-screen bg-background text-foreground">
                    <AppContent systemStatus={systemStatus} />
                    <Toaster position="bottom-right" richColors closeButton />
                  </div>
                </ConfirmProvider>
              </ProtectedRoute>
            </Router>
          </ErrorBoundary>
        </AppProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;
